import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { newsApi, type NewsListItem, type NewsReadRow } from '@/api/news'
import { getErrorMessage } from '@/utils/error'
import { formatDateDMY } from '@/utils/date'

interface NewsReadsDialogProps {
  open: boolean
  news: NewsListItem | null
  onClose: () => void
}

/** Статистика прочтений новости: кто и когда открыл. */
export function NewsReadsDialog({ open, news, onClose }: NewsReadsDialogProps) {
  const { t } = useTranslation()

  const [rows, setRows] = useState<NewsReadRow[]>([])
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 20, total: 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  useEffect(() => {
    if (!open || !news) return

    setLoading(true)
    setError(null)
    newsApi
      .getReads(news.id, { page: page + 1, per_page: rowsPerPage })
      .then((result) => {
        setRows(result.data)
        setMeta(result.meta)
      })
      .catch((err) => setError(getErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [open, news, page, rowsPerPage])

  useEffect(() => {
    if (open) {
      setPage(0)
    }
  }, [open, news])

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        {t('admin.news.readsTitle')}
        {news && (
          <Typography variant="body2" color="text.secondary">
            {news.title}
          </Typography>
        )}
        <IconButton
          onClick={onClose}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.news.readsUser')}</TableCell>
                  <TableCell>{t('admin.news.readsRoles')}</TableCell>
                  <TableCell>{t('admin.news.readsAt')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="text" width={200} /></TableCell>
                    <TableCell><Skeleton variant="text" width={90} /></TableCell>
                    <TableCell><Skeleton variant="text" width={90} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : meta.total === 0 ? (
          <Typography color="text.secondary">{t('admin.news.readsEmpty')}</Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('admin.news.readsUser')}</TableCell>
                    <TableCell>{t('admin.news.readsRoles')}</TableCell>
                    <TableCell>{t('admin.news.readsAt')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.user_id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar src={row.user?.avatar_url ?? undefined} sx={{ width: 32, height: 32 }}>
                            {row.user?.name?.[0] ?? '?'}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {row.user?.name ?? `#${row.user_id}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {row.user?.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(row.user?.roles ?? []).map((role) => (
                            <Chip key={role} size="small" variant="outlined" label={role} />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDateDMY(row.read_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, overflow: 'hidden', minWidth: 0 }}>
              <TablePagination
                component="div"
                count={meta.total}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage={t('admin.news.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) =>
                  t('admin.news.displayedRows', { from, to, count })
                }
              />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
